import dayjs from 'dayjs'

/**
 * Number of hours after an evening ends that it should still be visible in live dashboards.
 * This allows operators to continue viewing stats after an evening closes.
 */
export const EVENING_VISIBILITY_BUFFER_HOURS = 6

export interface RegionWithOperatingDate {
  _id: string
  test?: boolean
  currentOperatingDate?: {
    date: string
    eveningId?: number
    startTimestamp?: Date
    endTimestamp?: Date
  }
}

/**
 * Checks if a region is currently operating or recently operated.
 *
 * A region is considered "active" if:
 * 1. It's not a test region
 * 2. It has a current operating date with an eveningId
 * 3. Either:
 *    - The evening date is today, OR
 *    - We're within the operating window (start time to end time + buffer)
 *
 * @param region - The region to check
 * @param now - Current time (defaults to dayjs())
 * @returns true if the region is currently active
 */
export const isRegionCurrentlyActive = (region: RegionWithOperatingDate, now = dayjs()): boolean => {
  // In development mode, allow test regions for easier testing
  if (region.test && !import.meta.env.DEV) return false
  if (!region.currentOperatingDate?.eveningId) return false

  const date = dayjs(region.currentOperatingDate.date)
  const startHour = dayjs(region.currentOperatingDate.startTimestamp)
  const endTime = dayjs(region.currentOperatingDate.endTimestamp).add(EVENING_VISIBILITY_BUFFER_HOURS, 'hour')

  // Check if the evening is for today
  const isToday = date.isSame(now, 'day')

  // Check if we're within the operating window (including buffer after end)
  const isWithinOperatingWindow =
    startHour.isValid() && endTime.isValid() && now.isAfter(startHour) && now.isBefore(endTime)

  return isToday || isWithinOperatingWindow
}

/**
 * Filters an array of regions to only include those currently operating.
 *
 * @param regions - Array of regions to filter
 * @param now - Current time (defaults to dayjs())
 * @returns Filtered array of active regions
 */
export const filterActiveRegions = <T extends RegionWithOperatingDate>(regions: T[], now = dayjs()): T[] =>
  regions.filter((region) => isRegionCurrentlyActive(region, now))
